<template>
  <div class="compare-page">

    <!-- ===== انتخاب دو تیم ===== -->
    <div class="pick-box">
      <div class="pickers">
        <TeamPicker
            :label="teamA ? '' : 'تیم اول'"
            :value="teamA"
            :exclude-id="teamB ? teamB.id : null"
            @input="onPickA"
        />
        <div class="vs-badge">VS</div>
        <TeamPicker
            :label="teamB ? '' : 'تیم دوم'"
            :value="teamB"
            :exclude-id="teamA ? teamA.id : null"
            @input="onPickB"
        />
      </div>
      <p v-if="!teamA || !teamB" class="pick-hint">
        دو تیم را انتخاب کنید تا آمارشان رو در رو مقایسه شود
      </p>
    </div>

    <!-- ===== نتیجهٔ مقایسه ===== -->
    <div v-if="loading" class="state-box">
      <i class="fas fa-spinner fa-spin"></i>
      <p>در حال مقایسه...</p>
    </div>

    <div v-else-if="error" class="state-box error">
      <i class="fas fa-exclamation-circle"></i>
      <p>{{ error }}</p>
    </div>

    <template v-else-if="data && data.a && data.b">

      <!-- کارت تیم‌ها -->
      <div class="team-cards">
        <div class="team-card">
          <img v-if="data.a.logo" :src="data.a.logo" :alt="data.a.name"/>
          <div class="tc-name">{{ shortName(data.a) }}</div>
          <small v-if="data.a.area && data.a.area.name" class="tc-area">{{ data.a.area.name }}</small>
          <div v-if="data.a.rating != null" class="pes-badge" :class="tierClass(data.a.rating)">
            <b>{{ faNum(data.a.rating) }}</b>
            <small>قدرت تیم</small>
          </div>
        </div>
        <div class="tc-vs"></div>
        <div class="team-card">
          <img v-if="data.b.logo" :src="data.b.logo" :alt="data.b.name"/>
          <div class="tc-name">{{ shortName(data.b) }}</div>
          <small v-if="data.b.area && data.b.area.name" class="tc-area">{{ data.b.area.name }}</small>
          <div v-if="data.b.rating != null" class="pes-badge" :class="tierClass(data.b.rating)">
            <b>{{ faNum(data.b.rating) }}</b>
            <small>قدرت تیم</small>
          </div>
        </div>
      </div>

      <!-- حالت خالی صادقانه: هیچ آمار لیگ داخلی‌ای برای هیچ‌کدام ثبت نشده -->
      <div v-if="!hasAnyContent" class="state-box empty-stats">
        <i class="fas fa-database"></i>
        <p>آمار لیگ داخلی برای این دو تیم هنوز در دیتابیس ثبت نشده است.</p>
        <small>مقایسهٔ ضربه آزاد فقط از بازی‌های لیگ داخلی هر تیم استفاده می‌کند — نه جام‌ها و رقابت‌های بین‌کشوری</small>
      </div>

      <!-- فرم ۵ بازی اخیر -->
      <div class="panel" v-if="data.a.form.length || data.b.form.length">
        <h3 class="panel-title">
          <i class="fas fa-wave-square"></i>
          {{ formTitle }}
        </h3>
        <div class="form-grid">
          <div class="form-badges">
            <span v-if="!data.a.form.length" class="muted">—</span>
            <span
                v-for="(f, i) in data.a.form"
                :key="'fa' + i"
                :class="['fb', f.result.toLowerCase()]"
                :title="(f.atHome ? 'میزی: ' : 'بیرون: ') + f.score"
            >{{ f.result === 'W' ? 'ب' : f.result === 'D' ? 'م' : 'با' }}</span>
          </div>
          <span class="form-label">فرم اخیر</span>
          <div class="form-badges">
            <span v-if="!data.b.form.length" class="muted">—</span>
            <span
                v-for="(f, i) in data.b.form"
                :key="'fb' + i"
                :class="['fb', f.result.toLowerCase()]"
                :title="(f.atHome ? 'میزی: ' : 'بیرون: ') + f.score"
            >{{ f.result === 'W' ? 'ب' : f.result === 'D' ? 'م' : 'با' }}</span>
          </div>
        </div>
      </div>

      <!-- مقایسهٔ آماری ردیف‌به‌ردیف -->
      <div class="panel" v-if="metricRows.length">
        <h3 class="panel-title"><i class="fas fa-chart-bar"></i> مقایسهٔ آمار فصل</h3>
        <div class="metrics">
          <div
              v-for="row in metricRows"
              :key="row.key"
              class="metric-row"
              :class="{ 'rating-row': row.key === 'rating' }"
          >
            <div class="m-side" :class="{ lead: row.lead === 'a' }">{{ row.a }}</div>
            <div class="m-label">{{ row.label }}</div>
            <div class="m-side" :class="{ lead: row.lead === 'b' }">{{ row.b }}</div>
          </div>
        </div>
        <p class="season-note" v-if="data.a.seasonInfo || data.b.seasonInfo">
          <span v-if="data.a.seasonInfo"><b>منبع آمار {{ shortName(data.a) }}:</b> {{ data.a.seasonInfo.competitionName }}<template v-if="data.a.seasonInfo.seasonName"> — فصل {{ data.a.seasonInfo.seasonName }}</template><template v-if="data.a.season && data.a.season.scope === 'fallback'"> (خارج از لیگ داخلی)</template></span>
          <span v-if="data.a.seasonInfo && data.b.seasonInfo"> | </span>
          <span v-if="data.b.seasonInfo"><b>منبع آمار {{ shortName(data.b) }}:</b> {{ data.b.seasonInfo.competitionName }}<template v-if="data.b.seasonInfo.seasonName"> — فصل {{ data.b.seasonInfo.seasonName }}</template><template v-if="data.b.season && data.b.season.scope === 'fallback'"> (خارج از لیگ داخلی)</template></span>
        </p>
      </div>

      <!-- برترین‌های هر تیم -->
      <div
          class="panel"
          v-if="(data.a.scorers.length || data.a.assists.length || data.b.scorers.length || data.b.assists.length)"
      >
        <h3 class="panel-title"><i class="fas fa-star"></i> بهترین‌های فصل</h3>
        <div class="tops-grid">
          <div class="tops-col">
            <div v-if="data.a.scorers.length" class="top-group">
              <h4>⚽ گلزنان {{ shortName(data.a) }}</h4>
              <div v-for="(s, i) in data.a.scorers" :key="'as' + i" class="top-row" @click="goPlayer(s.player)">
                <span class="tr-rank">{{ faNum(i + 1) }}</span>
                <span class="tr-avatar" v-if="s.player.photo"><img :src="s.player.photo" :alt="s.player.name"/></span>
                <span v-else class="tr-avatar initials">{{ initials(s.player.name) }}</span>
                <span class="tr-name">{{ s.player.name }}</span>
                <b class="tr-val">{{ faNum(s.value) }} گل</b>
              </div>
            </div>
            <div v-if="data.a.assists.length" class="top-group">
              <h4>🅰 پاسورهای {{ shortName(data.a) }}</h4>
              <div v-for="(s, i) in data.a.assists" :key="'aa' + i" class="top-row" @click="goPlayer(s.player)">
                <span class="tr-rank">{{ faNum(i + 1) }}</span>
                <span class="tr-avatar" v-if="s.player.photo"><img :src="s.player.photo" :alt="s.player.name"/></span>
                <span v-else class="tr-avatar initials">{{ initials(s.player.name) }}</span>
                <span class="tr-name">{{ s.player.name }}</span>
                <b class="tr-val">{{ faNum(s.value) }} پاس</b>
              </div>
            </div>
            <p v-if="!data.a.scorers.length && !data.a.assists.length" class="muted center">رویداد گل/پاسی برای {{ shortName(data.a) }} در دیتابیس ثبت نشده</p>
          </div>

          <div class="tops-col">
            <div v-if="data.b.scorers.length" class="top-group">
              <h4>⚽ گلزنان {{ shortName(data.b) }}</h4>
              <div v-for="(s, i) in data.b.scorers" :key="'bs' + i" class="top-row" @click="goPlayer(s.player)">
                <span class="tr-rank">{{ faNum(i + 1) }}</span>
                <span class="tr-avatar" v-if="s.player.photo"><img :src="s.player.photo" :alt="s.player.name"/></span>
                <span v-else class="tr-avatar initials">{{ initials(s.player.name) }}</span>
                <span class="tr-name">{{ s.player.name }}</span>
                <b class="tr-val">{{ faNum(s.value) }} گل</b>
              </div>
            </div>
            <div v-if="data.b.assists.length" class="top-group">
              <h4>🅰 پاسورهای {{ shortName(data.b) }}</h4>
              <div v-for="(s, i) in data.b.assists" :key="'ba' + i" class="top-row" @click="goPlayer(s.player)">
                <span class="tr-rank">{{ faNum(i + 1) }}</span>
                <span class="tr-avatar" v-if="s.player.photo"><img :src="s.player.photo" :alt="s.player.name"/></span>
                <span v-else class="tr-avatar initials">{{ initials(s.player.name) }}</span>
                <span class="tr-name">{{ s.player.name }}</span>
                <b class="tr-val">{{ faNum(s.value) }} پاس</b>
              </div>
            </div>
            <p v-if="!data.b.scorers.length && !data.b.assists.length" class="muted center">رویداد گل/پاسی برای {{ shortName(data.b) }} در دیتابیس ثبت نشده</p>
          </div>
        </div>
      </div>

      <!-- رویارویی‌های مستقیم -->
      <div class="panel" v-if="data.h2h && data.h2h.played">
        <h3 class="panel-title"><i class="fas fa-exchange-alt"></i> رویارویی‌های مستقیم</h3>

        <div class="h2h-summary">
          <div class="hs-side">
            <b>{{ faNum(data.h2h.aWins) }}</b>
            <small>برد {{ shortName(data.a) }}</small>
          </div>
          <div class="hs-mid">
            <b>{{ faNum(data.h2h.draws) }}</b>
            <small>مساوی</small>
          </div>
          <div class="hs-side">
            <b>{{ faNum(data.h2h.bWins) }}</b>
            <small>برد {{ shortName(data.b) }}</small>
          </div>
        </div>

        <div
            v-for="m in data.h2h.matches"
            :key="m.id"
            class="h2h-row"
            @click="$router.push(`/match/${m.id}`)"
        >
          <div class="h2h-team">
            <img v-if="m.home && m.home.team && m.home.team.logo" :src="m.home.team.logo"/>
            <span :class="{ winner: m.home && m.home.isWinner }">{{ m.home ? shortName(m.home.team) : '—' }}</span>
          </div>
          <div class="h2h-center">
            <strong>{{ m.home ? m.home.score : 0 }} - {{ m.away ? m.away.score : 0 }}</strong>
            <small>{{ faDate(m.kickoffAt) }}</small>
          </div>
          <div class="h2h-team">
            <span :class="{ winner: m.away && m.away.isWinner }">{{ m.away ? shortName(m.away.team) : '—' }}</span>
            <img v-if="m.away && m.away.team && m.away.team.logo" :src="m.away.team.logo"/>
          </div>
        </div>
      </div>

    </template>

  </div>
</template>

<script>
import TeamPicker from '@/components/TeamPicker.vue'
import apiService from '@/services/api'
import { teamNames } from '@/utils/translations'
import { setSeo } from '@/utils/seo'

export default {
  name: 'TeamCompare',
  components: { TeamPicker },

  data() {
    return {
      teamA: null,
      teamB: null,
      data: null,
      loading: false,
      error: null,
    }
  },

  computed: {
    // عنوان پنل فرم — وقتی هر دو تیم فرم لیگ دارند، صریح بگو
    formTitle() {
      if (!this.data) return 'فرم ۵ بازی اخیر'
      const both = this.data.a.formScope === 'league' && this.data.b.formScope === 'league'
      return both ? 'فرم ۵ بازی اخیر در لیگ' : 'فرم ۵ بازی اخیر (همهٔ رقابت‌ها)'
    },

    // آیا کوچک‌ترین محتوایی برای نمایش هست؟ (برای پیام صادقانهٔ خالی)
    hasAnyContent() {
      if (!this.data || !this.data.a || !this.data.b) return false
      const a = this.data.a, b = this.data.b
      return !!(a.rating != null || b.rating != null
        || a.summary || b.summary
        || (a.form && a.form.length) || (b.form && b.form.length)
        || (a.scorers && a.scorers.length) || (a.assists && a.assists.length)
        || (b.scorers && b.scorers.length) || (b.assists && b.assists.length)
        || (this.data.h2h && this.data.h2h.played))
    },

    // ردیف‌های مقایسه — امتیاز قدرت اول، بعد آمار جدول (فقط موارد داده‌دار)
    metricRows() {
      if (!this.data || !this.data.a || !this.data.b) return []
      const A = this.data.a.summary, B = this.data.b.summary
      const rows = []

      // ۱) امتیاز قدرت PES (۱ تا ۱۰۰)
      const rA = this.data.a.rating, rB = this.data.b.rating
      if (rA != null || rB != null) {
        const fa = v => (v == null ? '—' : this.faNum(v))
        let lead = null
        if (rA != null && rB != null && rA !== rB) lead = rA > rB ? 'a' : 'b'
        rows.push({ key: 'rating', label: 'امتیاز قدرت (از ۱۰۰)', a: fa(rA), b: fa(rB), lead })
      }

      if (!A && !B) return rows
      const g = (s, k, dflt = '—') => (s && s[k] != null && s[k] !== '' ? this.faNum(s[k]) : dflt)
      const num = (s, k) => (s && s[k] != null ? Number(s[k]) : null)

      const def = [
        { key: 'position', label: 'رتبه در جدول', lowerBetter: true },
        { key: 'points', label: 'امتیاز' },
        { key: 'played', label: 'بازی' },
        { key: 'won', label: 'برد' },
        { key: 'draw', label: 'مساوی' },
        { key: 'lost', label: 'باخت', lowerBetter: true },
        { key: 'goalsFor', label: 'گل زده' },
        { key: 'goalsAgainst', label: 'گل خورده', lowerBetter: true },
        { key: 'goalDifference', label: 'تفاضل گل' },
      ]

      for (const d of def) {
        const a = num(A, d.key), b = num(B, d.key)
        if (a == null && b == null) continue
        let lead = null
        if (a != null && b != null && a !== b) {
          lead = (d.lowerBetter ? a < b : a > b) ? 'a' : 'b'
        }
        rows.push({ key: d.key, label: d.label, a: g(A, d.key), b: g(B, d.key), lead })
      }
      return rows
    },
  },

  watch: {
    // لینک عمیق /compare?a=&b= و تغییر query
    '$route.query'() {
      this.applyQuery()
    },
  },

  async created() {
    setSeo({
      title: 'مقایسه دو تیم | آمار رو در رو | ضربه آزاد',
      description: 'مقایسه آماری دو تیم فوتبال: جدول، فرم، گلزنان و پاسورها و رویارویی‌های مستقیم در ضربه آزاد',
      path: '/compare',
    })
    await this.applyQuery()
  },

  methods: {
    faNum(n) {
      if (n === null || n === undefined || n === '') return '—'
      return String(n).replace(/\d/g, d => '۰۱۲۳۴۵۶۷۸۹'[d])
    },

    initials(name) {
      return (name || '؟').trim().slice(0, 2)
    },

    shortName(t) {
      if (!t) return ''
      return teamNames[t.name] || t.faName || t.shortName || t.name || ''
    },

    // پلهٔ رنگی بج امتیاز PES
    tierClass(r) {
      if (r >= 85) return 'tier-gold'
      if (r >= 70) return 'tier-green'
      if (r >= 50) return 'tier-blue'
      return 'tier-gray'
    },

    faDate(d) {
      if (!d) return ''
      return new Date(d).toLocaleDateString('fa-IR', { year: 'numeric', month: 'long', day: 'numeric' })
    },

    goPlayer(p) {
      if (p && p.slug) this.$router.push(`/player/${p.slug}`)
    },

    onPickA(team) {
      this.teamA = team
      this.refresh()
    },

    onPickB(team) {
      this.teamB = team
      this.refresh()
    },

    // همگام‌سازی URL با انتخاب‌ها (لینک‌شدنی)
    syncUrl() {
      const q = {}
      if (this.teamA) q.a = this.teamA.slug || this.teamA.id
      if (this.teamB) q.b = this.teamB.slug || this.teamB.id
      const current = this.$route.query
      if ((current.a || '') !== (q.a || '') || (current.b || '') !== (q.b || '')) {
        this.$router.replace({ query: q }).catch(() => {})
      }
    },

    async applyQuery() {
      const { a, b } = this.$route.query
      if (!a && !b) return
      let needFetch = false
      if (a && (!this.teamA || String(this.teamA.slug || this.teamA.id) !== String(a))) {
        needFetch = true
        this.teamA = { slug: a, id: isNaN(Number(a)) ? null : Number(a), name: a }
      }
      if (b && (!this.teamB || String(this.teamB.slug || this.teamB.id) !== String(b))) {
        needFetch = true
        this.teamB = { slug: b, id: isNaN(Number(b)) ? null : Number(b), name: b }
      }
      if (needFetch || !this.data) await this.fetchCompare()
    },

    async refresh() {
      this.syncUrl()
      if (this.teamA && this.teamB) await this.fetchCompare()
    },

    async fetchCompare() {
      const a = this.teamA ? (this.teamA.slug || this.teamA.id) : null
      const b = this.teamB ? (this.teamB.slug || this.teamB.id) : null
      if (!a || !b) return
      this.loading = true
      this.error = null
      try {
        const data = await apiService.compareTeams(a, b)
        if (!data || !data.a || !data.b) {
          this.error = (data && data.error) || 'مقایسه این دو تیم ممکن نشد'
          this.data = null
        } else {
          this.data = data
          // با رسیدن نام واقعی، عنوان صفحه و URL را به‌روز کن
          this.teamA = { ...this.teamA, ...data.a }
          this.teamB = { ...this.teamB, ...data.b }
          this.syncUrl()
          setSeo({
            title: `${this.shortName(data.a)} در مقابل ${this.shortName(data.b)} | مقایسه دو تیم | ضربه آزاد`,
            description: `مقایسه آماری ${this.shortName(data.a)} و ${this.shortName(data.b)}: جدول، فرم، بهترین گلزنان و پاسورها و رویارویی‌های مستقیم`,
            path: this.$route.fullPath,
          })
        }
      } catch (e) {
        this.error = 'خطا در دریافت مقایسه'
        this.data = null
      } finally {
        this.loading = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '@/assets/sass/variables';
@import '@/assets/sass/mixin';

.compare-page {
  width:100%;
  margin: 0 auto;
  padding: 16px 12px 40px;
}

/* ===== انتخاب ===== */
.pick-box {
  background: #fff;
  border-radius: 16px;
  padding: 18px;
  box-shadow: $shadow-sm;
  margin-bottom: 18px;
}

.pickers {
  display: flex;
  align-items: flex-end;
  gap: 14px;
}

.vs-badge {
  flex-shrink: 0;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: linear-gradient(135deg, #047857, #10b981);
  color: #fff;
  font-weight: 900;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.pick-hint {
  margin: 12px 4px 0;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
}

/* ===== حالت‌ها ===== */
.state-box {
  background: #fff;
  border-radius: 16px;
  padding: 40px 20px;
  text-align: center;
  color: #94a3b8;

  i { font-size: 26px; margin-bottom: 10px; display: inline-block; }

  &.error { color: #dc2626; }
}

.muted { color: #94a3b8; font-size: 13px; }
.center { text-align: center; }

/* حالت خالی صادقانه */
.empty-stats {
  margin-bottom: 16px;

  small {
    display: block;
    margin-top: 8px;
    color: #cbd5e1;
    font-size: 11.5px;
    max-width: 420px;
    margin-inline: auto;
    line-height: 1.8;
  }
}

/* ===== کارت تیم‌ها ===== */
.team-cards {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
  background: 
    url('~@/assets/images/header-ball-bg.svg') no-repeat left 4% center / 240px auto,
    linear-gradient(135deg, #022c22 0%, #064e3b 45%, #047857 85%, #059669 100%);
  border-radius: 18px;
  padding: 22px 16px;
  margin-bottom: 16px;
  position: relative;
  overflow: hidden;
}

.team-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  min-width: 0;

  img {
    width: 64px;
    height: 64px;
    object-fit: contain;
    background: #fff;
    border-radius: 50%;
    padding: 4px;
  }
}

.tc-name {
  color: #fff;
  font-weight: 800;
  font-size: 15px;
  text-align: center;
}

.tc-area { color: rgba(255,255,255,.7); font-size: 12px; }

.tc-vs {
  color: rgba(255,255,255,.85);
  font-weight: 900;
  font-size: 18px;
  white-space: nowrap;
}

/* ===== بج امتیاز قدرت (سبک PES) ===== */
.pes-badge {
  margin-top: 8px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: #fff;
  border: 2.5px solid rgba(255,255,255,.9);
  box-shadow: 0 4px 14px rgba(15, 23, 42, .35);

  b { font-size: 21px; line-height: 1; font-weight: 900; }
  small { font-size: 8.5px; opacity: .92; margin-top: 2px; }

  &.tier-gold { background: linear-gradient(135deg, #b45309, #f59e0b); }
  &.tier-green { background: linear-gradient(135deg, #047857, #10b981); }
  &.tier-blue { background: linear-gradient(135deg, #1e40af, #3b82f6); }
  &.tier-gray { background: linear-gradient(135deg, #475569, #94a3b8); }
}

/* ===== پنل‌ها ===== */
.panel {
  background: #fff;
  border-radius: 16px;
  padding: 16px;
  box-shadow: $shadow-sm;
  margin-bottom: 16px;
}

.panel-title {
  margin: 0 0 14px;
  font-size: 15px;
  color: #111827;

  i { color: #3b82f6; margin-left: 6px; }
}

/* ===== فرم ===== */
.form-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
}

.form-badges {
  display: flex;
  gap: 6px;
  justify-content: center;
  flex-wrap: wrap;
}

.form-label { color: #94a3b8; font-size: 12px; }

.fb {
  width: 26px;
  height: 26px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 800;
  color: #fff;

  &.w { background: #10b981; }
  &.d { background: #f59e0b; }
  &.l { background: #ef4444; }
}

/* ===== مقایسه آماری ===== */
.metrics {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.metric-row {
  display: grid;
  grid-template-columns: 1fr minmax(90px, auto) 1fr;
  align-items: center;
  gap: 10px;
  background: $gray-50;
  border-radius: 10px;
  padding: 9px 12px;
}

.m-side {
  font-weight: 800;
  font-size: 15px;
  color: #374151;
  text-align: center;

  &.lead {
    color: #059669;
  }
}

.m-label {
  text-align: center;
  color: #6b7280;
  font-size: 12.5px;
  white-space: nowrap;
}

/* ردیف ویژهٔ امتیاز قدرت */
.metric-row.rating-row {
  background: linear-gradient(90deg, #fef3c7, #fffbeb, #fef3c7);
  border: 1px solid #fcd34d;

  .m-label { color: #92400e; font-weight: 800; }

  .m-side {
    font-size: 17px;
    color: #b45309;

    &.lead { color: #92400e; }
  }
}

.season-note {
  margin: 12px 2px 0;
  color: #94a3b8;
  font-size: 11.5px;
}

/* ===== برترین‌ها ===== */
.tops-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.top-group {
  margin-bottom: 14px;

  h4 {
    margin: 0 0 8px;
    font-size: 13px;
    color: #374151;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.top-row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 10px;
  cursor: pointer;
  transition: background .15s;

  &:hover { background: $gray-50; }
}

.tr-rank {
  width: 20px;
  color: #94a3b8;
  font-size: 12px;
  font-weight: 700;
  flex-shrink: 0;
}

.tr-avatar {
  width: 28px;
  height: 28px;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 50%;
  }

  &.initials {
    display: flex;
    align-items: center;
    justify-content: center;
    background: #e0e7ff;
    color: #1e40af;
    border-radius: 50%;
    font-size: 11px;
    font-weight: 700;
  }
}

.tr-name {
  font-size: 13px;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.tr-val {
  margin-right: auto;
  color: #059669;
  font-size: 12.5px;
  flex-shrink: 0;
}

/* ===== H2H ===== */
.h2h-summary {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 10px;
  align-items: center;
  background: $gray-50;
  border-radius: 12px;
  padding: 12px;
  margin-bottom: 12px;
}

.hs-side, .hs-mid {
  text-align: center;

  b { font-size: 20px; color: #111827; display: block; }
  small { color: #94a3b8; font-size: 11.5px; }
}

.h2h-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 10px;
  padding: 10px 8px;
  border-top: 1px solid $gray-100;
  cursor: pointer;
  transition: background .15s;

  &:hover { background: $gray-50; }
}

.h2h-team {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;

  &:last-child { justify-content: flex-end; }

  img { width: 24px; height: 24px; object-fit: contain; flex-shrink: 0; }

  span {
    font-size: 13px;
    color: #374151;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.winner { color: #059669; font-weight: 800; }
  }
}

.h2h-center {
  text-align: center;
  flex-shrink: 0;

  strong { display: block; font-size: 14px; color: #111827; }
  small { color: #94a3b8; font-size: 11px; }
}

/* ===== موبایل ===== */
@media (max-width: 640px) {
  .pickers { flex-direction: column; align-items: stretch; }

  .vs-badge {
    align-self: center;
    margin-bottom: 0;
    margin-top: -6px;
  }

  .tops-grid { grid-template-columns: 1fr; }

  .team-card img { width: 52px; height: 52px; }

  .pes-badge {
    width: 50px;
    height: 50px;

    b { font-size: 17px; }
  }

  .tc-name { font-size: 13px; }

  .m-side { font-size: 13.5px; }
}
</style>
